<template>
    <UiModal
        :title="title"
        :has-header="false"
        :has-close="false"
        @close="onCancel"
    >
        <span>
            {{ props.message ? props.message : $t('GLOBAL.ARE_YOU_SURE') }}
        </span>

        <template v-if="props.type === DialogType.Prompt">
            <UiInputText
                v-model="prompt"
                v-bind="props.inputProps"
            />
        </template>

        <template #footer>
            <div class="flex justify-end items-center mr-2">
                <UiButton
                    :label="props.cancelText ? props.cancelText : $t('GLOBAL.CANCEL')"
                    :disabled="isLoading"
                    data-e2e="dialog-cancel-button"
                    variant="default"
                    size="small"
                    class="mx-1"
                    @click="onCancel"
                />
                <UiButton
                    :label="props.confirmText ? props.confirmText : $t('GLOBAL.CONFIRM')"
                    :disabled="isLoading"
                    :variant="buttonVariant"
                    data-e2e="dialog-confirm-button"
                    size="small"
                    class="mx-1"
                    @click="onConfirm"
                />
            </div>
        </template>
    </UiModal>
</template>

<script lang="ts" setup>
    import { DialogVariant, DialogType, ButtonVariant } from '@/enums/ui.enum';

    import UiModal from '@/components/Global/Modals/UiModal/UiModal.vue';
    import UiButton from '@/components/Global/UiButton/UiButton.vue';
    import UiInputText from '@/components/Global/UiFormInputs/UiInputText/UiInputText.vue';

    const props = withDefaults(
        defineProps<{
            type?: `${DialogType}`;
            title?: string;
            variant?: `${DialogVariant}`;
            message?: string;
            inputProps?: {
                placeholder?: string;
                value?: string;
            };
            cancelText?: string;
            confirmText?: string;
        }>(),
        {
            type: 'confirm',
            title: 'Confirm',
            variant: 'primary',
            message: '',
            cancelText: '',
            confirmText: '',
        },
    );

    const emit = defineEmits<{
        cancel: [];
        confirm: [val?: any];
    }>();

    const { t } = useI18n();
    const prompt = ref<any>();
    const isLoading = ref<boolean>(false);

    const onCancel = () => {
        emit('cancel');
        isLoading.value = true;
    };

    const onConfirm = () => {
        let value;

        if (props.type === DialogType.Prompt) {
            value = prompt.value;
        }

        emit('confirm', value);
        isLoading.value = true;
    };

    const title = computed(() => {
        if (props.title) {
            return props.title;
        }

        switch (props.type) {
            case DialogType.Confirm:
                return t('GLOBAL.CONFIRM');
            case DialogType.Prompt:
                return t('GLOBAL.INFORM');
            case DialogType.Alert:
                return t('GLOBAL.ALERT');
            default:
                return undefined;
        }
    });

    const buttonVariant = computed<ButtonVariant>(() => {
        switch (props.variant) {
            case DialogVariant.Primary:
                return ButtonVariant.Primary;
            case DialogVariant.Success:
                return ButtonVariant.Success;
            case DialogVariant.Warning:
                return ButtonVariant.Warning;
            case DialogVariant.Danger:
                return ButtonVariant.Danger;
            default:
                return ButtonVariant.Primary;
        }
    });

    if (props.type === DialogType.Prompt && props.inputProps?.value) {
        prompt.value = props.inputProps?.value ?? '';
    }
</script>
